<template>
    <div class="downgradePlan">
        <form-message v-if="stripeError" :message="stripeError" />

        <div class="downgradePlan__title">Are you sure you want to downgrade to Pay-As-You-Go?</div>

        <div class="downgradePlanBox downgradePlanBox--features">
            <div class="downgradePlanBox__content">
                <div class="downgradePlanBox__title">
                    Here are the features you'll lose when you downgrade...
                </div>

                <template v-for="(item, index) in features">
                    <div v-if="item.show && !loading" :key="index" class="downgradePlanBox__item">
                        <div class="downgradePlanBox__info">
                            <span class="downgradePlanBox__icon">
                                <fa-icon :icon="['far', 'times']" />
                            </span>
                            <span
                                v-if="item.id !== 'broadcasts' && item.id !== 'compliance'"
                                class="downgradePlanBox__count"
                            >
                                {{ item.count }}
                            </span>
                            <span class="downgradePlanBox__text">
                                <template
                                    v-if="
                                        ['broadcasts', 'compliance', 'customDomain'].includes(
                                            item.id
                                        )
                                    "
                                >
                                    {{ item.title }}
                                </template>
                                <template v-else-if="item.id === 'inboxes'">
                                    {{ item.title }}(es)
                                </template>
                                <template v-else> {{ item.title }}(s) </template>
                                <span
                                    v-if="
                                        !['conversations', 'compliance', 'customDomain'].includes(
                                            item.id
                                        )
                                    "
                                >
                                    (limit: {{ item.limits }})
                                </span>
                            </span>
                        </div>
                        <span class="downgradePlanBox__more" @click="moreHandler(index)">
                            More Info
                        </span>
                    </div>
                </template>
            </div>
        </div>

        <template v-if="!isSubscription">
            <div class="columns">
                <div class="column">
                    <p class="field">
                        <b>Add Payment Information</b>
                    </p>

                    <form-input
                        v-model="stripeForm.name"
                        label="Card Holder Name"
                        placeholder="Card Holder Name"
                        :error="stripeForm.errors.get('name')"
                        @input="stripeForm.errors.clear('name')"
                    />

                    <div class="field">
                        <label class="label"> Card Details </label>
                        <div class="control">
                            <div id="create-card" class="input" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <p
            v-if="
                isSubscription &&
                    subscriptionStatus !== 'trialing' &&
                    subscriptionStatus !== 'canceled'
            "
            class="downgradePlan__desc"
        >
            Your account will be downgraded from the {{ subscription.plan.name }} ({{
                $filters.number(subscription.plan_credits)
            }}
            credits) to the <span style="white-space: nowrap">Pay-As-You-Go</span> plan at the end
            of your billing cycle on {{ currentDateFormatted }}
        </p>

        <checkbox
            class="downgradePlan__checkbox"
            :label="'Yes, I understand that I will lose access to these features.'"
            @handle-change="disabledButton = !disabledButton"
        />

        <div class="downgradePlan__wrapBtn">
            <div class="downgradePlan__boxBtn">
                <a href="#" class="button" @click="back"> Back </a>
            </div>

            <div class="downgradePlan__boxBtn">
                <a href="#" :class="classesClose" @click="close"> Keep my account </a>
                <button :class="classesSubmit" :disabled="disabledButton" @click="submit">
                    Downgrade
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import EarlyUpgradeMixin from 'mixins/early-upgrade';
import OrganizationMixin from 'mixins/organization';
import CardsMixin from 'mixins/cards';
import { USER_ROLES } from 'store/modules/user';

export default {
    emits: ['close', 'handleChangeData'],

    mixins: [EarlyUpgradeMixin, OrganizationMixin, CardsMixin],

    props: {
        show: { type: Boolean, default: false },
        isCanceled: { type: Boolean, default: false },
    },

    data() {
        return {
            form: new Form(
                {
                    token: '',
                },
                false
            ),
            stripeForm: new Form(
                {
                    name: '',
                    city: '',
                    region: '',
                    zip: '',
                },
                false
            ),
            stripeError: null,
            hideFooter: true,
            loading: false,
            disabledButton: true,
            downgradeConversetionCount: null,
            features: [
                {
                    id: 'contacts',
                    title: 'Contact',
                    link: '/contacts',
                    limits: 250,
                    count: null,
                    show: false,
                },
                {
                    id: 'inboxes',
                    title: 'Inbox',
                    link: '/settings/organization/inboxes',
                    limits: 1,
                    count: null,
                    show: false,
                },
                {
                    id: 'cannedMessages',
                    title: 'Saved Reply',
                    link: '/settings/personal/settings/canned-messages',
                    limits: 1,
                    count: null,
                    show: false,
                },
                {
                    id: 'triggers',
                    title: 'Trigger',
                    link: '/triggers',
                    limits: 1,
                    count: null,
                    show: false,
                },
                {
                    id: 'numbers',
                    title: 'Numbers',
                    link: '/settings/organization/numbers',
                    limits: 1,
                    count: null,
                    show: false,
                },
                {
                    id: 'conversations',
                    title: 'Conversations',
                    link: '/conversations',
                    limits: null,
                    show: false,
                },
                {
                    id: 'broadcasts',
                    title: 'Broadcasts',
                    link: '/broadcasts',
                    limits: 1,
                    show: true,
                },
                {
                    id: 'compliance',
                    title: 'Compliance',
                    link: '/settings/organization/compliance',
                    show: false,
                },
                {
                    id: 'customDomain',
                    title: 'Custom Domain',
                    link: '/settings/organization/custom-url-shortener',
                    count: null,
                    show: false,
                },
            ],
        };
    },

    computed: {
        classesClose() {
            return {
                button: true,
                'button-cancel': true,
                'is-disabled': this.loading,
            };
        },

        classesSubmit() {
            return {
                button: true,
                'is-danger': true,
                'is-disabled': this.loading || this.disabledButton,
                'is-busy': this.loading,
            };
        },

        currentDateFormatted() {
            return moment.utc(this.subscriptionPeriodEndsAt).format('MMMM D, Y');
        },

        ...mapState(stores.Inboxes, ['inboxes', 'inboxesArray']),
        ...mapState(stores.Contacts, ['contacts', 'contactsPagination']),
        ...mapState(stores.Messages, ['paginationCanned']),
        ...mapState(stores.CannedMessages, ['cannedMessages']),
        ...mapState(stores.Triggers, ['triggers']),
        ...mapState(stores.Members, ['members']),
        ...mapState(stores.Invitations, ['invitations']),
        ...mapState(stores.ComplianceProfile, ['complianceBillingInfo']),
        ...mapState(stores.Number, ['unassignedNumbers', 'numbers']),
        ...mapState(stores.Shortener, ['currentCustomDomain']),
    },

    methods: {
        async init() {
            await this.initData();

            if (!this.isSubscription) {
                this.initStripe();
                this.initStripeCard('#create-card');
            }
        },

        async initData() {
            try {
                this.loading = true;

                await Promise.all([
                    this.$store.dispatch('getOrganizationInvites'),
                    this.$store.dispatch('contactsLoad'),
                    this.$store.dispatch('cannedMessagesLoad'),
                    this.$store.dispatch('triggersLoad'),
                    this.$store.dispatch('getOrganizationMembers'),
                    this.$store.dispatch('billingComplianceInfo'),
                    this.$store.dispatch('getNumbers'),
                    this.$store.dispatch('getNumbersUnassigned'),
                    this.$store.dispatch('getCustomDomain', false),
                ]);

                const resDowngradeConversationStatistic = await this.$store.dispatch(
                    'getConversationsDowngradeStatistic'
                );
                this.downgradeConversetionCount =
                    resDowngradeConversationStatistic?.conversation_closed_count || null;

                await this.initTotal();
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'init');
            } finally {
                this.loading = false;
            }
        },

        initTotal() {
            const filterSeats = this.members.filter(
                (o) => o.role !== 'owner' && o.role !== USER_ROLES.VIEW_ONLY
            );
            const filterPending = this.invitations.filter(
                (o) => o.user.role !== USER_ROLES.VIEW_ONLY
            );

            const numbersCount = this.numbers?.length || 0;
            const unassignedNumbersCount = this.unassignedNumbers?.length || 0;
            const totalNumbers = numbersCount + unassignedNumbersCount;

            this.features.forEach((item) => {
                if (
                    item.id === 'contacts' &&
                    this.contacts &&
                    this.contactsPagination.total - item.limits > 0
                ) {
                    item.show = true;
                    item.count = this.contactsPagination.total - item.limits;
                } else if (
                    item.id === 'conversations' &&
                    this.downgradeConversetionCount &&
                    this.downgradeConversetionCount > 0
                ) {
                    item.show = true;
                    item.count = this.downgradeConversetionCount;
                } else if (
                    item.id === 'numbers' &&
                    totalNumbers &&
                    totalNumbers - item.limits > 0
                ) {
                    item.show = true;
                    item.count = totalNumbers - item.limits;
                } else if (
                    item.id === 'inboxes' &&
                    this.inboxesArray &&
                    this.inboxesArray.length - item.limits > 0
                ) {
                    item.show = true;
                    item.count = this.inboxesArray.length - item.limits;
                } else if (
                    item.id === 'cannedMessages' &&
                    this.cannedMessages &&
                    this.cannedMessages.length - item.limits > 0
                ) {
                    item.show = true;
                    item.count = this.cannedMessages.length - item.limits;
                } else if (
                    item.id === 'triggers' &&
                    this.triggers &&
                    this.triggers.length - item.limits > 0
                ) {
                    item.show = true;
                    item.count = this.triggers.length - item.limits;
                } else if (
                    item.id === 'seats' &&
                    filterSeats &&
                    filterSeats.length + filterPending.length > 0
                ) {
                    item.show = true;
                    item.count = filterSeats.length + filterPending.length;
                } else if (item.id === 'compliance' && this.complianceBillingInfo.subscribed) {
                    item.show = true;
                } else if (item.id === 'broadcasts') {
                    item.show = this.isSubscription;
                } else if (item.id === 'customDomain') {
                    item.show = this.currentCustomDomain[0]?.status === 'active';
                }
            });
        },

        close(e) {
            if (this.loading) return;

            this.hideEarlyDowngrade();
            this.$emit('close');
        },

        async submit(e) {
            if (this.loading) return;

            try {
                const isSubscription = this.isSubscription;

                this.loading = true;

                if (isSubscription) {
                    await this.$store.dispatch('updateBillingPayAsYouGoMicroservice');
                } else {
                    await this.handleNotExistCard();
                }

                // update global data
                await this.$store.dispatch('getUserMe');
                await this.$store.dispatch('accountBalanceUpdated', {
                    account_balance: this.organization.account_balance,
                    account_credits: this.organization.account_credits,
                    trial_credits: this.organization.trial_credits,
                });

                // finish
                this.$emit('handleChangeData', ['form', 'successful', true]);
                setTimeout(() => {
                    this.$emit('handleChangeData', ['form', 'successful', false]);
                    this.close();

                    if (isSubscription) {
                        if (this.isCanceled) {
                            window.location = '/';
                        }
                    } else {
                        this.$router.replace('/register');
                    }
                }, 1500);
            } catch (e) {
                if (e.error) {
                    this.stripeError = e.error.message;
                } else {
                    this.stripeError =
                        e.message || 'Failed to save your payment method, please try again.';
                }

                this.$log.error(e, this.$options.__file, 'submit');
            } finally {
                this.loading = false;
            }
        },

        async handleNotExistCard() {
            this.stripeError = null;

            if (!this.stripeForm.name) {
                this.stripeForm.errors.record({ name: ['Card Holder Name is required'] });
                this.loading = false;
            } else {
                this.stripeForm.errors.clear();
            }

            const result = await this.stripe.createToken(this.stripeCard, {
                name: `${this.stripeForm.name.trim()}`,
                address_city: this.stripeForm.city,
                address_state: this.stripeForm.region,
                address_country: this.stripeForm.country,
            });

            if (result.error) throw result;
            if (!this.stripeForm.name) {
                this.loading = false;

                return;
            }

            this.form.token = result.token.id;

            await this.$store.dispatch('updateBillingUpgradePlanMicroservice', {
                params: {
                    plan: 'free-plan-monthly',
                    token: this.form.token,
                },
            });
        },

        back(e) {
            e.preventDefault();

            this.hideEarlyDowngrade();
        },

        moreHandler(index) {
            this.$router.push(this.features[index].link);
            this.close();
        },
    },

    mounted() {
        this.init();
    },
};
</script>
